/* Bernard James — PDP purchase-area behaviours (Dev QA sheet, QA-PDP).
   Loaded by sections/main-product.liquid for the editorial-split layout only.

   1. <bj-pdp-sticky-atc>  mobile (<990px) sticky Add to bag.
   2. Fold fit             mobile (<=749px) hero height so Add to bag is in the
                           first screen.
   3. Trust-bar timing     keeps the trust bar's delivery timing in step with
                           the selected variant.

   Nothing here adds to the cart or decides availability itself: the sticky
   bar clicks the main form's submit, and the timing is server-rendered by the
   same stock logic as the IN STOCK / MADE TO ORDER rows (main-product.liquid).
   Each step is isolated so one failing cannot take the others down. */
(function () {
  'use strict';

  var STICKY_QUERY = window.matchMedia('(max-width: 989px)');
  var FOLD_QUERY = window.matchMedia('(max-width: 749px)');

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function visible(el) {
    return !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  }

  function focusQuietly(el) {
    if (!el || !el.focus) return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  function onMediaChange(query, fn) {
    if (query.addEventListener) query.addEventListener('change', fn);
    else if (query.addListener) query.addListener(fn);
  }

  /* ------------------------------------------------ 1. sticky Add to bag */

  var StickyBase = typeof HTMLElement === 'function' ? HTMLElement : function () {};

  class BjPdpStickyAtc extends StickyBase {
    connectedCallback() {
      if (this._ready) return;
      this.sectionId = this.dataset.section;
      this.form = document.getElementById('product-form-' + this.sectionId);
      this.mainButton = this.form && this.form.querySelector('[name="add"]');
      if (!this.mainButton) return; // gift card / no form: stay hidden
      this._ready = true;

      this.buyGroup = this.mainButton.closest('[data-bj-buy], .product__buy_buttons') || this.mainButton;
      this.button = this.querySelector('[data-bj-sticky-button]');
      this.label = this.querySelector('[data-bj-sticky-label]');
      this.priceEl = this.querySelector('[data-bj-sticky-price]');
      this.statusEl = this.querySelector('[data-bj-sticky-status]');
      this.buyInView = true;
      this.footerNear = false;

      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'true');
      this.button.tabIndex = -1;
      this.button.addEventListener('click', this.onClick.bind(this));

      this.scheduleSync = this.scheduleSync.bind(this);
      this.watchVisibility();
      this.watchState();
      onMediaChange(STICKY_QUERY, this.updateVisibility.bind(this));
      this.sync();
      this.updateVisibility();
    }

    /* Visibility: shown only once the main buy group has scrolled ABOVE the
       viewport (not merely "not visible" — on load it can sit below the fold),
       hidden again when it is back in view or once the footer group reaches
       the upper half of the screen, so the bar never sits over the footer's
       links, newsletter or legal line.
       On a short PDP (clamped description, no recommendations) those two
       ranges can meet, and then the bar simply never needs to appear. */
    watchVisibility() {
      var self = this;
      if (typeof IntersectionObserver !== 'function') return;

      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.buyInView = entry.isIntersecting || entry.boundingClientRect.top > 0;
        });
        self.updateVisibility();
      }).observe(this.buyGroup);

      var footer = document.querySelector('.footer-group, .shopify-section-group-footer-group, footer');
      if (footer) {
        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              self.footerNear = entry.isIntersecting;
            });
            self.updateVisibility();
          },
          { rootMargin: '0px 0px -50% 0px' }
        ).observe(footer);
      }
    }

    updateVisibility() {
      var show = STICKY_QUERY.matches && !this.buyInView && !this.footerNear;
      if (show === this.classList.contains('is-visible')) return;
      this.classList.toggle('is-visible', show);
      // Keep the off-screen bar out of the tab order and the a11y tree.
      if (show) this.removeAttribute('aria-hidden');
      else this.setAttribute('aria-hidden', 'true');
      this.button.tabIndex = show ? 0 : -1;
    }

    /* State mirroring. Everything the bar shows is read from the main form and
       the blocks global.js re-renders on variant change, so it can never
       disagree with them. */
    watchState() {
      if (typeof MutationObserver !== 'function') return;
      var observer = new MutationObserver(this.scheduleSync);
      observer.observe(this.mainButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'data-bj-was-disabled'],
        childList: true,
        subtree: true,
        characterData: true
      });
      var sid = this.sectionId;
      [
        document.getElementById('price-' + sid),
        document.getElementById('FulfillmentNote-' + sid),
        document.getElementById('variant-radios-' + sid)
      ].forEach(function (el) {
        if (el) {
          observer.observe(el, {
            attributes: true,
            attributeFilter: ['class'],
            childList: true,
            subtree: true,
            characterData: true
          });
        }
      });
      document.addEventListener('variant:change', this.scheduleSync);
    }

    scheduleSync() {
      if (this._pending) return;
      this._pending = true;
      var self = this;
      requestAnimationFrame(function () {
        self._pending = false;
        self.sync();
      });
    }

    // A required option still on its "Select size" placeholder
    // (assets/bj-size-select.js marks the fieldset .select--placeholder and
    // holds the main button disabled until a choice is made).
    pendingOption() {
      var info = document.getElementById('ProductInfo-' + this.sectionId) || document;
      return info.querySelector('[data-select-placeholder].select--placeholder');
    }

    // bj-size-select.js parks the real disabled state in data-bj-was-disabled
    // while its placeholder lock is on; read through it, as that script does.
    soldOut() {
      var b = this.mainButton;
      if (b.dataset.bjWasDisabled !== undefined) return b.dataset.bjWasDisabled === 'true';
      return b.disabled;
    }

    currentPrice() {
      var price = document.querySelector('#price-' + this.sectionId + ' .price');
      if (!price) return '';
      var wrapper = price.closest('.price-wrapper, [id^="price-"]');
      if (wrapper && wrapper.classList.contains('visibility-hidden')) return '';
      var item = price.classList.contains('price--on-sale')
        ? price.querySelector('.price__sale .price-item--sale')
        : price.querySelector('.price__regular .price-item--regular');
      return item ? item.textContent.trim() : '';
    }

    // Made-to-order messaging: the fulfillment note's heading ("Made to
    // order"), present only when that note applies to the selected variant.
    currentStatus() {
      var note = document.getElementById('FulfillmentNote-' + this.sectionId);
      if (!note) return '';
      var heading = note.querySelector('.product__fulfillment-note-heading');
      var text = note.querySelector('.product__fulfillment-note-text');
      return ((heading || text || {}).textContent || '').trim();
    }

    sync() {
      if (!this._ready) return;
      var pending = this.pendingOption();
      var soldOut = this.soldOut();
      var loading =
        this.mainButton.getAttribute('aria-disabled') === 'true' || this.mainButton.classList.contains('loading');
      var mainLabel = this.mainButton.querySelector('span');

      var text;
      if (pending && !soldOut) {
        text = pending.getAttribute('data-select-placeholder') || this.dataset.chooseLabel || '';
      } else {
        text = mainLabel ? mainLabel.textContent.trim() : '';
      }
      if (text && this.label.textContent !== text) this.label.textContent = text;

      this.state = soldOut ? 'unavailable' : pending ? 'choose' : 'ready';
      this.button.disabled = this.state === 'unavailable';
      this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.classList.toggle('is-loading', loading);
      this.dataset.state = this.state;

      // SOLDOUT-PRICE: the price block is empty while a sold-out variant is
      // selected (settings.bj_hide_sold_out_price), so the bar shows no price
      // either, and gets it back with the block on the next available variant.
      var price = this.currentPrice();
      if (this.priceEl.textContent !== price) this.priceEl.textContent = price;
      this.priceEl.hidden = !price;

      var status = soldOut ? '' : this.currentStatus();
      this.statusEl.textContent = status;
      this.statusEl.hidden = !status;
    }

    onClick(event) {
      event.preventDefault();
      this.sync();
      if (this.state === 'choose') {
        this.revealOption(this.pendingOption());
        return;
      }
      if (this.state !== 'ready' || this.mainButton.disabled) return;
      if (this.mainButton.getAttribute('aria-disabled') === 'true') return; // add in flight
      // The main submit: same form, same variant id, same properties, same
      // bag drawer. product-form.js handles the rest.
      this.mainButton.click();
    }

    revealOption(fieldset) {
      if (!fieldset) return;
      var header = document.querySelector('.shopify-section-header, sticky-header');
      var offset = header ? header.getBoundingClientRect().height : 0;
      var top = fieldset.getBoundingClientRect().top + window.pageYOffset - offset - 24;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });

      // Open the size list so the choice is one tap away, and move focus there
      // for keyboard and screen-reader users. The list's .active toggle belongs
      // to the theme's own dropdown handler, so we click its trigger rather
      // than set the class.
      // Deferred: this runs inside the sticky button's own click, which is
      // still on its way up to document — where global.js closes every open
      // dropdown on any click. Opening synchronously would be undone by that
      // same event a moment later.
      setTimeout(function () {
        var trigger = fieldset.querySelector('.dropdown-select .select-label');
        var list = fieldset.querySelector('.dropdown-select ul');
        if (trigger && list && !list.classList.contains('active')) trigger.click();
        var target = fieldset.querySelector('.dropdown-select ul.active input:not(.disabled)');
        focusQuietly(target);
        if (!target || document.activeElement !== target) {
          if (!fieldset.hasAttribute('tabindex')) fieldset.setAttribute('tabindex', '-1');
          focusQuietly(fieldset);
        }
      }, 0);
    }
  }

  if (window.customElements && !customElements.get('bj-pdp-sticky-atc')) {
    customElements.define('bj-pdp-sticky-atc', BjPdpStickyAtc);
  }

  /* ------------------------------------------------------- 2. fold fit */

  // The hero's CSS height is min(430:538, max(min, 100svh - reserve)); the
  // reserve is everything that must fit in the first screen besides the hero:
  // the page above it and the info column down to the bottom of the first
  // visible buy button, plus breathing room. Liquid seeds an estimate; this
  // replaces it with the measured value. Independent of the hero's current
  // height, so re-measuring never feeds back on itself.
  var FOLD_BREATHING_ROOM = 16;

  function mediaList(product) {
    var lists = product.querySelectorAll('.product__media-list');
    for (var i = 0; i < lists.length; i++) {
      if (visible(lists[i])) return lists[i];
    }
    return null;
  }

  function firstVisible(root, selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var found = root.querySelectorAll(selectors[i]);
      for (var j = 0; j < found.length; j++) {
        if (visible(found[j]) && found[j].getBoundingClientRect().height > 0) return found[j];
      }
    }
    return null;
  }

  function fitFold() {
    each(document.querySelectorAll('[data-bj-fold-fit]'), function (product) {
      if (!FOLD_QUERY.matches) return;
      var list = mediaList(product);
      var cta = firstVisible(product, [
        '.product-form__submit',
        '.product-form__secondary-submit',
        '.notify-open-button'
      ]);
      if (!list || !cta) return;
      var scrollY = window.pageYOffset || 0;
      var ctaBottom = cta.getBoundingClientRect().bottom + scrollY;
      var mediaHeight = list.getBoundingClientRect().height;
      var reserve = Math.round(ctaBottom - mediaHeight + FOLD_BREATHING_ROOM);
      if (reserve > 0) product.style.setProperty('--bj-pdp-fold-reserve', reserve + 'px');
    });
  }

  /* ---------------------------------------------- 3. trust-bar timing */

  function syncTrustTiming(root) {
    each((root || document).querySelectorAll('[data-bj-trust]'), function (bar) {
      var group = bar.closest('.product__buy_buttons') || document;
      var source = group.querySelector('[data-bj-trust-timing]');
      var target = bar.querySelector('[data-bj-trust-timing-target]');
      if (!target) return;
      var text = source ? source.textContent.trim() : '';
      if (target.textContent.trim() !== text) target.textContent = text;
      target.hidden = !text;
    });
  }

  function watchTrustTiming() {
    if (typeof MutationObserver !== 'function') return;
    each(document.querySelectorAll('[data-bj-trust]'), function (bar) {
      if (bar.dataset.bjTrustReady === 'true') return;
      bar.dataset.bjTrustReady = 'true';
      var group = bar.closest('.product__buy_buttons');
      // #StockStatus-<section> is re-rendered by global.js on every variant
      // change; it survives as an element, so the observer stays attached.
      var wrapper = group && group.querySelector('[id^="StockStatus-"]');
      if (!wrapper) return;
      new MutationObserver(function () {
        syncTrustTiming(group);
      }).observe(wrapper, { childList: true, subtree: true, characterData: true });
    });
  }

  /* ------------------------------------------------------------ wiring */

  function step(name, fn) {
    try {
      fn();
    } catch (err) {
      console.error('[bj-pdp-purchase] ' + name + ' failed', err);
    }
  }

  function init() {
    step('fitFold', fitFold);
    step('watchTrustTiming', watchTrustTiming);
    step('syncTrustTiming', syncTrustTiming);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Fonts and the swatch row settle after DOMContentLoaded; re-measure once.
  window.addEventListener('load', function () {
    step('fitFold', fitFold);
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      step('fitFold', fitFold);
    });
  }

  // Width changes only: mobile browsers fire resize as the URL bar collapses,
  // and re-fitting then would make the hero jump mid-scroll. The CSS uses svh
  // for the same reason.
  var lastWidth = window.innerWidth;
  var resizeTimer;
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      step('fitFold', fitFold);
    }, 150);
  });

  document.addEventListener('shopify:section:load', init);
})();
